import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
  // 部分接口（如 aikey 的 models/test/batchtest）失败时返回的是
  // message / status / url 字段，status 是上游 API 的 HTTP 状态码
  message?: string;
  status?: number;
  url?: string;
}

// 全局错误提示统一使用这个 key：同一时刻只保留一条请求错误提示，
// 连续失败时新提示替换旧提示，不会越堆越多。
const REQUEST_ERROR_KEY = 'request-error';

// 后端没返回 errorMessage 时的兜底文案，避免弹出空白提示
const FALLBACK_ERROR_MESSAGE = '操作失败，请稍后重试';

/**
 * 给错误对象打上 handled 标记：表示全局 errorHandler 已经处理过
 * （弹出提示 / 静默 / 跳转登录页）。页面 catch 里的兜底提示
 * （src/utils/errorToast.ts 的 toastError）看到该标记就不会再重复弹窗。
 */
const markHandled = (error: any) => {
  if (error && typeof error === 'object') {
    error.handled = true;
  }
};

/**
 * 从 axios 错误的响应体里提取错误文案（HTTP 非 2xx 的场景）。
 * 后端 http.Error 有两种体：纯文本（如「数据库错误」）和 JSON 字符串
 * （如 ApiTask 的 {"success":false,"message":"请求格式错误"}）。
 */
const extractResponseMessage = (respData: any): string => {
  if (typeof respData === 'string') {
    const text = respData.trim();
    if (text.startsWith('{') && text.endsWith('}')) {
      try {
        const parsed = JSON.parse(text);
        return parsed?.errorMessage || parsed?.message || text;
      } catch {
        return text;
      }
    }
    return text;
  }
  if (respData && typeof respData === 'object') {
    return respData.errorMessage || respData.message || '';
  }
  return '';
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType, message, status, url } =
        res as unknown as ResponseStructure;
      if (!success) {
        // 后端有两种失败格式：约定的 errorMessage/errorCode，以及 aikey 等
        // 接口的 message/status/url。这里把 message 归一化成 errorMessage，
        // 让 errorHandler 能展示真实报错原因而不是兜底文案。
        // 注意：status 是上游 API 的 HTTP 状态码，不能并入 errorCode——
        // errorCode === 401 代表会话过期要跳登录页，上游 401 只是 Key 无效。
        const msg = errorMessage || message;
        const error: any = new Error(msg);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage: msg, showType, data, status, url };
        error.data = res; // 保留后端原始响应，方便调用方获取完整数据
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode, status } = errorInfo;
          // 优先展示后端返回的 errorMessage / message；
          // aikey 这类透传上游错误的响应再补上上游 HTTP 状态码，方便定位
          let toastContent = errorMessage || FALLBACK_ERROR_MESSAGE;
          if (status !== undefined && status !== errorCode) {
            toastContent += `（HTTP ${status}）`;
          }
          // 会话过期（Cookie 失效）：清掉本地用户态，跳回登录页并带上回跳地址
          if (errorCode === 401) {
            markHandled(error);
            if (window.location.pathname !== '/user/login') {
              const redirect = encodeURIComponent(
                window.location.pathname + window.location.search,
              );
              window.location.href = `/user/login?redirect=${redirect}`;
            }
            return;
          }
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // 后端要求静默：不弹窗，但同样标记已处理，页面兜底也不再弹
              markHandled(error);
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning({ content: toastContent, key: REQUEST_ERROR_KEY });
              markHandled(error);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error({ content: toastContent, key: REQUEST_ERROR_KEY });
              markHandled(error);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: toastContent,
                message: errorCode,
              });
              markHandled(error);
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error({ content: toastContent, key: REQUEST_ERROR_KEY });
              markHandled(error);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        if (error.response.status === 401) {
          // HTTP 401 同样视为会话过期
          markHandled(error);
          if (window.location.pathname !== '/user/login') {
            const redirect = encodeURIComponent(
              window.location.pathname + window.location.search,
            );
            window.location.href = `/user/login?redirect=${redirect}`;
          }
          return;
        }
        // 优先展示响应体里的错误文案（http.Error 的纯文本 / JSON 里的 message），
        // 拿不到时才退回只显示状态码
        const status = error.response.status;
        const respMsg = extractResponseMessage(error.response.data);
        message.error({
          content: respMsg
            ? `${respMsg}（HTTP ${status}）`
            : `Response status:${status}`,
          key: REQUEST_ERROR_KEY,
        });
        markHandled(error);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error({ content: 'None response! Please retry.', key: REQUEST_ERROR_KEY });
        markHandled(error);
      } else {
        // 发送请求时出了点问题
        message.error({ content: 'Request error, please retry.', key: REQUEST_ERROR_KEY });
        markHandled(error);
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const url = config?.url?.concat();
      return { ...config, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      // 注意：不要在 success === false 时在这里弹窗。
      // 失败响应统一交给下面的 errorHandler 处理，它会用后端返回的 errorMessage
      // 提示，并按 showType 决定是否静默（SILENT）。这里再弹一次会重复。
      return response;
    },
  ],
};
